import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { PieChart, Pie, Cell, XAxis, YAxis, ResponsiveContainer, Tooltip, AreaChart, Area } from "recharts";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, X } from "lucide-react";
import api from "@/lib/api";
import { STATUSES, STATUS_COLORS, PRIORITY_COLORS, isOverdue, timeAgo, tzOffset } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { useLiveRefresh } from "@/hooks/useLiveRefresh";
import Skeleton from "@/components/tracker/Skeleton";
import Avatar from "@/components/tracker/Avatar";
import OnlineStrip from "@/components/tracker/OnlineStrip";

const EASE = [0.22, 1, 0.36, 1];

const CountUp = ({ value, duration = 900 }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (typeof value !== "number") return;
    let raf, start;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(value * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <>{typeof value === "number" ? n : value}</>;
};

const StatCard = ({ label, value, accent, testid, delay, loading }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7, delay, ease: EASE }}
    className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-md"
    data-testid={testid}
  >
    <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/40">{label}</p>
    {loading ? (
      <Skeleton className="mt-3 h-9 w-16" />
    ) : (
      <p className="font-display mt-3 text-4xl font-black tracking-tight" style={{ color: accent || "#fff" }}>
        <CountUp value={value} />
      </p>
    )}
  </motion.div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [activityRange, setActivityRange] = useState("today");
  const [activityCounts, setActivityCounts] = useState({ today: 0, week: 0, month: 0, all: 0 });
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [momentumOwner, setMomentumOwner] = useState("all");
  const [statsProject, setStatsProject] = useState("all");
  const isAdmin = user.role === "admin";

  const loadCore = useCallback(() => {
    api.get("/stats", { params: { tz_offset: tzOffset(), project: statsProject } }).then((res) => setStats(res.data)).catch(() => {});
    api.get("/projects").then((res) => setProjects(res.data)).catch(() => {});
    if (isAdmin) api.get("/tasks").then((res) => setTasks(res.data)).catch(() => {});
  }, [isAdmin, statsProject]);
  useEffect(loadCore, [loadCore]);

  const loadActivity = useCallback(() => {
    const params = { range: activityRange, limit: 100, tz_offset: tzOffset() };
    if (statsProject !== "all") params.project = statsProject;
    api.get("/activity", { params }).then((res) => setActivity(res.data)).catch(() => {});
  }, [activityRange, statsProject]);
  useEffect(loadActivity, [loadActivity]);

  const refreshAll = useCallback(() => { loadCore(); loadActivity(); }, [loadCore, loadActivity]);
  useLiveRefresh(refreshAll);

  useEffect(() => {
    const params = { tz_offset: tzOffset() };
    if (statsProject !== "all") params.project = statsProject;
    api.get("/activity/counts", { params }).then((res) => setActivityCounts(res.data)).catch(() => {});
  }, [statsProject, activity]);

  const MOMENTUM_COLORS = ["#90C2E7", "#D8B4E2", "#FFD3B6", "#FFC3A0", "#FF8B94", "#A8E6CF"];
  const ownerNames = stats ? stats.by_owner.map((o) => o.name) : [];
  const chartData = stats
    ? momentumOwner === "all"
      ? stats.weekly_done
      : stats.weekly_done_by_owner?.[momentumOwner] || []
    : [];
  const chartColor =
    momentumOwner === "all" ? "#00FF66" : MOMENTUM_COLORS[Math.max(0, ownerNames.indexOf(momentumOwner)) % MOMENTUM_COLORS.length];

  const donutData = stats
    ? STATUSES.map((s) => ({ name: s, value: stats.by_status[s] || 0, color: STATUS_COLORS[s] })).filter((d) => d.value > 0)
    : [];
  const owners = stats?.by_owner ?? [];
  const maxOwnerCount = Math.max(1, ...owners.map((o) => o.count));

  const projectName = (id) => projects.find((p) => p.id === id)?.name || "—";
  const daysOverdue = (due) => Math.max(1, Math.floor((Date.now() - new Date(due + "T00:00:00").getTime()) / 86400000));
  const overdueTasks = isAdmin
    ? tasks
        .filter(isOverdue)
        .filter((t) => statsProject === "all" || t.project_id === statsProject)
        .sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""))
    : [];

  return (
    <div data-testid="dashboard-page">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }} className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#00FF66]">Overview</p>
          <h1 className="font-display mt-2 text-3xl font-bold tracking-tight md:text-4xl">
            Welcome back, {user.name.split(" ")[0]}.
          </h1>
          <div className="mt-4">
            <OnlineStrip />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">Project</label>
          <select
            value={statsProject}
            onChange={(e) => setStatsProject(e.target.value)}
            className={`rounded-xl border px-3.5 py-2 text-sm font-semibold outline-none transition-colors duration-200 [color-scheme:dark] ${statsProject !== "all" ? "border-[#FFB020]/50 bg-[#FFB020]/10 text-[#FFB020]" : "border-white/10 bg-white/5 text-white focus:border-[#00FF66]/60"}`}
            data-testid="dashboard-project-filter"
          >
            <option value="all" className="bg-[#0A120D]">All projects</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id} className="bg-[#0A120D]">{p.name}</option>
            ))}
          </select>
          {statsProject !== "all" && (
            <button
              onClick={() => setStatsProject("all")}
              className="flex items-center gap-1 rounded-xl border border-[#FFB020]/40 px-3 py-2 text-sm font-semibold text-[#FFB020] transition-colors duration-200 hover:bg-[#FFB020] hover:text-black"
              data-testid="dashboard-clear-filters"
            >
              <X className="h-4 w-4" />
              Clear
            </button>
          )}
        </div>
      </motion.div>

      <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-5">
        <StatCard label="Total tasks" value={stats?.total ?? "—"} testid="stat-total-tasks" delay={0.05} loading={!stats} />
        <StatCard label="Done" value={stats?.done ?? "—"} accent="#A8E6CF" testid="stat-done" delay={0.12} loading={!stats} />
        <StatCard label="In progress" value={stats ? stats.by_status["IN PROGRESS"] : "—"} accent="#90C2E7" testid="stat-in-progress" delay={0.19} loading={!stats} />
        <StatCard label="Urgent open" value={stats?.urgent_open ?? "—"} accent="#FFD3B6" testid="stat-urgent-open" delay={0.26} loading={!stats} />
        <div className={stats?.overdue > 0 ? "animate-overdue rounded-2xl" : ""}>
          <StatCard label="Overdue" value={stats?.overdue ?? "—"} accent="#FF8B94" testid="stat-overdue" delay={0.33} loading={!stats} />
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
          className="rounded-3xl border border-white/10 bg-white/[0.03] p-6"
          data-testid="dashboard-donut"
        >
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-white/40">Status breakdown</p>
          <div className="relative h-[306px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={donutData}
                  dataKey="value"
                  innerRadius="58%"
                  outerRadius="85%"
                  strokeWidth={0}
                  paddingAngle={3}
                >
                  {donutData.map((entry) => (
                    <Cell
                      key={entry.name}
                      fill={entry.color}
                      style={{ cursor: "pointer" }}
                      onClick={() => navigate(`/app/tasks?status=${encodeURIComponent(entry.name)}`)}
                      data-testid={`donut-slice-${entry.name.toLowerCase().replace(/\s+/g, "-")}`}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: "#0A120D", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12 }}
                  itemStyle={{ color: "#fff" }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center" data-testid="donut-center">
              <span className="font-display text-3xl font-black tracking-tight">{stats?.total ?? 0}</span>
              <span className="text-[9px] font-bold uppercase tracking-[0.24em] text-white/35">tasks</span>
              <span className="mt-1.5 rounded-full bg-[#A8E6CF]/10 px-2.5 py-0.5 text-[10px] font-black text-[#A8E6CF]">
                {stats?.total ? Math.round(((stats?.done ?? 0) / stats.total) * 100) : 0}% done
              </span>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap justify-center gap-x-5 gap-y-2" data-testid="donut-legend">
            {STATUSES.map((s) => {
              const count = stats?.by_status[s] ?? 0;
              const pct = stats?.total ? Math.round((count / stats.total) * 100) : 0;
              return (
                <span key={s} className="flex items-center gap-1.5 text-[10px] font-semibold text-white/60" data-testid={`legend-${s.toLowerCase().replace(/\s+/g, "-")}`}>
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: STATUS_COLORS[s] }} />
                  {s}
                  <span className="font-black text-white/90">{count}</span>
                  <span className="text-white/35">({pct}%)</span>
                </span>
              );
            })}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.38, ease: EASE }}
          className="rounded-3xl border border-white/10 bg-white/[0.03] p-6"
          data-testid="dashboard-owner-bars"
        >
          <div className="mb-4 flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/40">Assignment summary</p>
            <span className="text-[10px] font-semibold text-white/25">click a person to view their tasks</span>
          </div>
          {!stats ? (
            <div className="h-[306px] space-y-4 pr-1" data-testid="assignment-skeleton">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i}>
                  <div className="mb-1.5 flex items-center justify-between">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-4 w-16" />
                  </div>
                  <Skeleton className="h-2.5 w-full rounded-full" />
                </div>
              ))}
            </div>
          ) : owners.length === 0 ? (
            <p className="py-10 text-center text-sm text-white/30" data-testid="assignment-empty">No tasks assigned yet.</p>
          ) : (
            <div className="h-[306px] space-y-4 overflow-y-auto pr-1" data-testid="assignment-list">
              {owners.map((o) => {
                const pctDone = o.count ? Math.round((o.done / o.count) * 100) : 0;
                const segments = STATUSES.filter((s) => o.by_status[s] > 0);
                return (
                  <button
                    key={o.name}
                    onClick={() => navigate(`/app/tasks?owner=${encodeURIComponent(o.name)}`)}
                    className="group block w-full text-left"
                    data-testid={`assignment-owner-${o.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <div className="mb-1.5 flex items-center justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-2">
                        <Avatar name={o.name} size={22} />
                        <span className="truncate text-sm font-bold text-white/85 transition-colors duration-200 group-hover:text-[#00FF66]">
                          {o.name}
                        </span>
                        {o.overloaded && (
                          <span className="flex shrink-0 items-center gap-1 rounded-full border border-[#FF8B94]/50 bg-[#FF8B94]/10 px-2 py-0.5 text-[8px] font-black uppercase tracking-wider text-[#FF8B94]" data-testid={`assignment-overloaded-${o.name.toLowerCase().replace(/\s+/g, "-")}`}>
                            <AlertTriangle className="h-2.5 w-2.5" /> Overloaded
                          </span>
                        )}
                        {o.on_pto && (
                          <span className="shrink-0 rounded-full border border-[#FFD3B6]/50 bg-[#FFD3B6]/10 px-2 py-0.5 text-[8px] font-black uppercase tracking-wider text-[#FFD3B6]" data-testid={`assignment-pto-${o.name.toLowerCase().replace(/\s+/g, "-")}`}>
                            On PTO
                          </span>
                        )}
                        {o.overdue > 0 && (
                          <span className="shrink-0 rounded-full bg-[#FF8B94] px-2 py-0.5 text-[8px] font-black uppercase tracking-wider text-black" data-testid={`assignment-overdue-${o.name.toLowerCase().replace(/\s+/g, "-")}`}>
                            {o.overdue} overdue
                          </span>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-2 text-xs">
                        <span className="font-black text-white/80">{o.count}</span>
                        <span className="text-white/30">{pctDone}% done</span>
                      </div>
                    </div>
                    <div className="h-2.5 w-full overflow-hidden rounded-full bg-white/[0.04]">
                      <div className="flex h-full" style={{ width: `${(o.count / maxOwnerCount) * 100}%` }}>
                        {segments.map((s, i) => (
                          <div
                            key={s}
                            className={`h-full ${i === 0 ? "rounded-l-full" : ""} ${i === segments.length - 1 ? "rounded-r-full" : ""}`}
                            style={{ width: `${(o.by_status[s] / o.count) * 100}%`, backgroundColor: STATUS_COLORS[s] }}
                            title={`${s}: ${o.by_status[s]}`}
                          />
                        ))}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          <div className="mt-5 flex flex-wrap justify-center gap-x-4 gap-y-1.5 border-t border-white/10 pt-4" data-testid="assignment-legend">
            {STATUSES.map((s) => (
              <span key={s} className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wide text-white/45">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: STATUS_COLORS[s] }} />
                {s}
              </span>
            ))}
          </div>
        </motion.div>
      </div>

      {isAdmin && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: EASE }}
          className="mt-8 rounded-3xl border border-white/10 bg-white/[0.03] p-6"
          data-testid="overdue-watchlist"
        >
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/40">Overdue watchlist</p>
              {overdueTasks.length > 0 && (
                <span className="rounded-full bg-[#FF8B94]/15 px-2.5 py-0.5 text-[10px] font-black text-[#FF8B94]" data-testid="overdue-watchlist-count">
                  {overdueTasks.length}
                </span>
              )}
            </div>
            <span className="text-[10px] font-semibold text-white/25">click a task to open it</span>
          </div>

          {overdueTasks.length === 0 ? (
            <p className="py-10 text-center text-sm text-white/30" data-testid="overdue-watchlist-empty">
              Nothing overdue — the team is on track.
            </p>
          ) : (
            <div className="max-h-[340px] space-y-2 overflow-y-auto pr-1" data-testid="overdue-watchlist-list">
              {overdueTasks.map((t) => (
                <button
                  key={t.id}
                  onClick={() => navigate(`/app/board?task=${t.id}`)}
                  className="group flex w-full items-center gap-3 rounded-2xl border border-white/5 bg-white/[0.02] px-4 py-3 text-left transition-colors duration-200 hover:border-[#FF8B94]/40 hover:bg-white/[0.04]"
                  data-testid={`overdue-task-${t.id}`}
                >
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: STATUS_COLORS[t.status] }} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-white/85 group-hover:text-white">{t.title}</p>
                    <p className="truncate text-[11px] text-white/40">
                      {t.owner_name || "Unassigned"} · {projectName(t.project_id)}
                    </p>
                  </div>
                  <span
                    className="shrink-0 rounded-full px-2 py-0.5 text-[9px] font-bold"
                    style={{ backgroundColor: `${PRIORITY_COLORS[t.priority] || "#888"}22`, color: PRIORITY_COLORS[t.priority] || "#aaa" }}
                  >
                    {t.priority}
                  </span>
                  <div className="w-24 shrink-0 text-right">
                    <p className="text-[11px] font-bold text-[#FF8B94]">{daysOverdue(t.due_date)}d overdue</p>
                    <p className="text-[10px] text-white/35">due {new Date(t.due_date + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.4, ease: EASE }}
        className="mt-8 rounded-3xl border border-white/10 bg-white/[0.03] p-6"
        data-testid="weekly-momentum"
      >
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/40">
            Momentum <span className="ml-2 normal-case tracking-normal text-white/25">tasks completed per week</span>
          </p>
          <div className="flex items-center gap-2" data-testid="momentum-filters">
            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: chartColor }} data-testid="momentum-color-dot" />
            <select
              value={momentumOwner}
              onChange={(e) => setMomentumOwner(e.target.value)}
              className="rounded-xl border border-white/10 bg-white/5 px-3.5 py-2 text-sm font-semibold text-white outline-none transition-colors duration-200 [color-scheme:dark] focus:border-[#00FF66]/60"
              data-testid="momentum-filter-select"
            >
              <option value="all" className="bg-[#0A120D]">Everyone</option>
              {ownerNames.map((name) => (
                <option key={name} value={name} className="bg-[#0A120D]">{name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="h-44">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: -22 }}>
              <defs>
                <linearGradient id="momentumFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={chartColor} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={chartColor} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="week" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ background: "#0A120D", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12 }}
                itemStyle={{ color: "#fff" }}
                cursor={{ stroke: "rgba(0,255,102,0.3)" }}
              />
              <Area type="monotone" dataKey="count" stroke={chartColor} strokeWidth={2.5} fill="url(#momentumFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.46, ease: EASE }}
        className="mt-8 rounded-3xl border border-white/10 bg-white/[0.03] p-6"
        data-testid="activity-feed"
      >
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/40">Activity</p>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5" data-testid="activity-range-filter">
              {[
                { key: "today", label: "Today" },
                { key: "week", label: "This Week" },
                { key: "month", label: "This Month" },
                { key: "all", label: "All" },
              ].map((r) => (
                <button
                  key={r.key}
                  onClick={() => setActivityRange(r.key)}
                  data-testid={`activity-range-${r.key}`}
                  className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold transition-colors duration-200 ${
                    activityRange === r.key
                      ? "bg-[#00FF66] text-black"
                      : "border border-white/15 text-white/50 hover:text-white"
                  }`}
                >
                  {r.label}
                  <span
                    data-testid={`activity-count-${r.key}`}
                    className={`min-w-[16px] rounded-full px-1 text-center text-[9px] leading-4 ${
                      activityRange === r.key ? "bg-black/25 text-black" : "bg-white/10 text-white/60"
                    }`}
                  >
                    {activityCounts[r.key] ?? 0}
                  </span>
                </button>
              ))}
            </div>
            <span className="animate-pulse-glow text-[10px] font-bold uppercase tracking-widest text-[#00FF66]">Live</span>
          </div>
        </div>
        {activity.length === 0 ? (
          <p className="py-6 text-center text-sm text-white/30" data-testid="activity-empty">
            {activityRange === "all" && statsProject === "all"
              ? "No activity yet — move a task to start the story."
              : "No activity for this filter."}
          </p>
        ) : (
          <ul className="ml-2 space-y-1 border-l border-white/10 pl-6">
            {activity.map((event) => (
              <li key={event.id} className="relative flex flex-wrap items-baseline gap-x-2 py-2" data-testid={`activity-${event.id}`}>
                <span
                  className={`absolute -left-[29px] top-3.5 h-2 w-2 rounded-full ${
                    event.action === "deleted" ? "bg-[#FF8B94]" : "bg-[#00FF66]"
                  }`}
                  aria-hidden="true"
                />
                <span className="text-sm font-bold text-white/85">{event.actor_name}</span>
                <span className="text-sm text-white/50">{event.action}</span>
                <span className="text-sm font-semibold text-white/80">{event.task_title}</span>
                {event.detail && <span className="text-xs text-white/35">({event.detail})</span>}
                <span className="ml-auto text-xs text-white/30">{timeAgo(event.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </motion.div>
    </div>
  );
}
